export type PromptInputSuggestion = {
  id: string;
  kind: "command" | "context";
  label: string;
  description?: string;
};

export type PromptInputPersistedState = {
  text: string;
  cursor?: number;
  attachments: Array<{ id: string; name: string; type: "file" | "image" }>;
};

export type PromptInputInteractionState = {
  mode: "normal" | "shell";
  popover:
    | { type: "closed" }
    | { type: "context"; query: string; activeID?: string }
    | { type: "command-inline"; query: string; activeID?: string }
    | { type: "command-menu"; query: string; activeID?: string };
  drag: "idle" | "active";
  focus: "editor" | "command-search" | "external";
  activeContextID?: string;
  historyIndex: number;
};

export type PromptInputInteractionEvent =
  | { type: "input.changed"; value: string; persist?: boolean }
  | { type: "commands.open" }
  | { type: "context.open" }
  | { type: "popover.query"; value: string }
  | { type: "popover.results"; ids: string[] }
  | { type: "popover.active"; id: string }
  | { type: "popover.close" }
  | { type: "popover.select"; item: PromptInputSuggestion }
  | { type: "key.down"; key: string; ctrl: boolean; composing: boolean; ids: string[]; empty?: boolean }
  | { type: "mode.shell" }
  | { type: "mode.normal" }
  | { type: "drag.enter" }
  | { type: "drag.leave" }
  | { type: "focus.editor" }
  | { type: "focus.external" };

export type PromptInputInteractionCommand =
  | { type: "draft.setText"; value: string }
  | { type: "mention.add"; item: PromptInputSuggestion }
  | { type: "popover.filter"; popover: "command" | "context"; query: string }
  | { type: "suggestion.select"; id: string }
  | { type: "focus.editor" };

export type PromptInputTransition = {
  state: PromptInputInteractionState;
  commands: PromptInputInteractionCommand[];
  handled: boolean;
};

export function createPromptInputInteractionState(): PromptInputInteractionState {
  return {
    mode: "normal",
    popover: { type: "closed" },
    drag: "idle",
    focus: "external",
    historyIndex: -1,
  };
}

export function transitionPromptInput(
  state: PromptInputInteractionState,
  event: PromptInputInteractionEvent,
  persisted: PromptInputPersistedState
): PromptInputTransition {
  if (event.type === "input.changed") return inputChanged(state, event.value, event.persist !== false, persisted.cursor);
  if (event.type === "commands.open") return openCommands(state, persisted);
  if (event.type === "context.open") return openContext(state, persisted);
  if (event.type === "popover.query") return queryChanged(state, event.value);
  if (event.type === "popover.results") return resultsChanged(state, event.ids);
  if (event.type === "popover.active") return activeChanged(state, event.id);
  if (event.type === "popover.close") return changed({ ...state, popover: { type: "closed" } });
  if (event.type === "popover.select") return suggestionSelected(state, event.item, persisted);
  if (event.type === "key.down") return keyDown(state, event);
  if (event.type === "mode.shell") return changed({ ...state, mode: "shell", popover: { type: "closed" } });
  if (event.type === "mode.normal") return changed({ ...state, mode: "normal" });
  if (event.type === "drag.enter") return changed({ ...state, drag: "active" });
  if (event.type === "drag.leave") return changed({ ...state, drag: "idle" });
  if (event.type === "focus.editor") return changed({ ...state, focus: "editor" });
  return changed({ ...state, focus: "external" });
}

function inputChanged(
  state: PromptInputInteractionState,
  value: string,
  persist: boolean,
  cursor: number | undefined
): PromptInputTransition {
  const setText: PromptInputInteractionCommand[] = persist ? [{ type: "draft.setText", value }] : [];
  if (state.mode === "normal" && value === "!") {
    return changed({ ...state, mode: "shell", popover: { type: "closed" }, focus: "editor" }, [
      { type: "draft.setText", value: "" },
    ]);
  }
  const context = value.slice(0, cursor ?? value.length).match(/(?:^|\s)@([^\s@]*)$/);
  if (context) {
    const query = context[1] ?? "";
    return changed({ ...state, popover: { type: "context", query }, focus: "editor" }, [
      ...setText,
      { type: "popover.filter", popover: "context", query },
    ]);
  }

  const command = value.match(/^\/(\S*)$/);
  if (command) {
    const query = command[1] ?? "";
    return changed({ ...state, popover: { type: "command-inline", query }, focus: "editor" }, [
      ...setText,
      { type: "popover.filter", popover: "command", query },
    ]);
  }

  return changed(
    { ...state, popover: state.popover.type === "command-menu" ? state.popover : { type: "closed" }, focus: "editor" },
    setText
  );
}

function openCommands(
  state: PromptInputInteractionState,
  persisted: PromptInputPersistedState
): PromptInputTransition {
  return changed({ ...state, popover: { type: "command-inline", query: "" }, focus: "editor" }, [
    { type: "draft.setText", value: persisted.text + "/" },
    { type: "popover.filter", popover: "command", query: "" },
    { type: "focus.editor" },
  ]);
}

function openContext(
  state: PromptInputInteractionState,
  persisted: PromptInputPersistedState
): PromptInputTransition {
  return changed({ ...state, popover: { type: "context", query: "" }, focus: "editor" }, [
    { type: "draft.setText", value: persisted.text + "@" },
    { type: "popover.filter", popover: "context", query: "" },
    { type: "focus.editor" },
  ]);
}

function queryChanged(state: PromptInputInteractionState, query: string): PromptInputTransition {
  if (state.popover.type === "closed") return unchanged(state);
  const popover = state.popover.type === "context" ? "context" : "command";
  return changed({ ...state, popover: { ...state.popover, query, activeID: undefined } }, [
    { type: "popover.filter", popover, query },
  ]);
}

function resultsChanged(state: PromptInputInteractionState, ids: string[]): PromptInputTransition {
  if (state.popover.type === "closed") return unchanged(state);
  const activeID = state.popover.activeID && ids.includes(state.popover.activeID) ? state.popover.activeID : ids[0];
  if (activeID === state.popover.activeID) return unchanged(state);
  return changed({ ...state, popover: { ...state.popover, activeID } });
}

function activeChanged(state: PromptInputInteractionState, id: string): PromptInputTransition {
  if (state.popover.type === "closed" || state.popover.activeID === id) return unchanged(state);
  return changed({ ...state, popover: { ...state.popover, activeID: id } });
}

function suggestionSelected(
  state: PromptInputInteractionState,
  item: PromptInputSuggestion,
  persisted: PromptInputPersistedState
): PromptInputTransition {
  const current = persisted.text;
  const commands: PromptInputInteractionCommand[] = [];
  if (item.kind === "command") {
    commands.push({
      type: "draft.setText",
      value: replaceTrigger(current, "/", `${item.label} `),
    });
  } else {
    commands.push({ type: "mention.add", item });
  }
  commands.push({ type: "focus.editor" });
  return changed({ ...state, popover: { type: "closed" }, focus: "editor" }, commands);
}

function keyDown(
  state: PromptInputInteractionState,
  event: Extract<PromptInputInteractionEvent, { type: "key.down" }>
): PromptInputTransition {
  if (event.ctrl && event.key.toLowerCase() === "g") {
    if (state.popover.type === "closed") return unchanged(state);
    return changed({ ...state, popover: { type: "closed" }, focus: "editor" }, [{ type: "focus.editor" }], true);
  }
  if (state.popover.type === "closed") {
    if (state.mode === "shell" && (event.key === "Escape" || (event.key === "Backspace" && event.empty))) {
      return changed({ ...state, mode: "normal" }, [], true);
    }
    return unchanged(state);
  }
  if (event.key === "Escape") {
    return changed({ ...state, popover: { type: "closed" }, focus: "editor" }, [{ type: "focus.editor" }], true);
  }
  if (event.key === "Tab" || (event.key === "Enter" && !event.composing)) {
    if (!state.popover.activeID) return unchanged(state, true);
    return unchanged(state, true, [{ type: "suggestion.select", id: state.popover.activeID }]);
  }
  const direction =
    event.key === "ArrowDown" || (event.ctrl && event.key === "n")
      ? 1
      : event.key === "ArrowUp" || (event.ctrl && event.key === "p")
        ? -1
        : 0;
  if (!direction || event.ids.length === 0) return unchanged(state);
  const current = state.popover.activeID ? event.ids.indexOf(state.popover.activeID) : -1;
  const index =
    current < 0
      ? direction === 1
        ? 0
        : event.ids.length - 1
      : (current + direction + event.ids.length) % event.ids.length;
  return changed({ ...state, popover: { ...state.popover, activeID: event.ids[index] } }, [], true);
}

function replaceTrigger(value: string, trigger: "@" | "/", replacement: string) {
  const index = trigger === "/" ? value.indexOf(trigger) : value.lastIndexOf(trigger);
  return index < 0 ? replacement : value.slice(0, index) + replacement;
}

function changed(
  state: PromptInputInteractionState,
  commands: PromptInputInteractionCommand[] = [],
  handled = false
): PromptInputTransition {
  return { state, commands, handled };
}

function unchanged(
  state: PromptInputInteractionState,
  handled = false,
  commands: PromptInputInteractionCommand[] = []
): PromptInputTransition {
  return { state, commands, handled };
}
